// The 12 canonical roles. Each user has exactly one platform-context
// role at a time (Guest / Member / Paid / Agency / Staff / Manager /
// Admin / Super Admin), plus project-scoped role(s) on individual
// projects (Owner / Invited User / Invited Viewer / Invited Expert).
//
// Role is *who you are right now*. Level is *how good you are in a
// domain*. The two are orthogonal: a Member can be Level 11 Guru in
// Security; an Admin might be Level 4 Adept in Cloud.

// Platform-scoped roles — exactly one of these per user account.
export const PLATFORM_ROLES = Object.freeze({
  GUEST:        'guest',         // ephemeral, browser-session only
  MEMBER:       'member',        // email-verified, free tier
  PAID_MEMBER:  'paid_member',   // subscription or non-zero PAYG balance
  AGENCY:       'agency',        // belongs to an Agency org
  STAFF:        'staff',         // Moksy support
  MANAGER:      'manager',       // Moksy team lead
  ADMIN:        'admin',         // platform administrator
  SUPER_ADMIN:  'super_admin'    // ultimate platform authority
});

// Project-scoped roles — granted per project to specific user accounts.
// Owner can hold simultaneously with any platform role; the rest are
// attached as a project_role on the user-project relationship.
export const PROJECT_ROLES = Object.freeze({
  OWNER:           'owner',
  INVITED_USER:    'invited_user',
  INVITED_VIEWER:  'invited_viewer',
  INVITED_EXPERT:  'invited_expert'
});

export const ALL_ROLES = Object.freeze({
  ...PLATFORM_ROLES,
  ...PROJECT_ROLES
});

// Convenience sets used by guards.
export const PLATFORM_ADMIN_ROLES = Object.freeze(
  new Set([PLATFORM_ROLES.ADMIN, PLATFORM_ROLES.SUPER_ADMIN])
);

export const PLATFORM_STAFF_ROLES = Object.freeze(
  new Set([
    PLATFORM_ROLES.STAFF,
    PLATFORM_ROLES.MANAGER,
    PLATFORM_ROLES.ADMIN,
    PLATFORM_ROLES.SUPER_ADMIN
  ])
);

// Has any paying relationship to the platform — used for entitlement
// checks ("can spend mox", "can request reviews"). Note: Agency members
// are paid by virtue of their org's seat.
export const PAYING_ROLES = Object.freeze(
  new Set([
    PLATFORM_ROLES.PAID_MEMBER,
    PLATFORM_ROLES.AGENCY,
    // Staff/admin can also act as members on their personal projects.
    PLATFORM_ROLES.STAFF,
    PLATFORM_ROLES.MANAGER,
    PLATFORM_ROLES.ADMIN,
    PLATFORM_ROLES.SUPER_ADMIN
  ])
);

export const isPlatformAdmin = (role) => PLATFORM_ADMIN_ROLES.has(role);
export const isSuperAdmin = (role) => role === PLATFORM_ROLES.SUPER_ADMIN;
export const isPlatformStaff = (role) => PLATFORM_STAFF_ROLES.has(role);
export const isPayingRole = (role) => PAYING_ROLES.has(role);
